import { useEffect, useState } from 'react';
import { ChartBarIcon, TableCellsIcon } from '@heroicons/react/24/outline';
import { TOGGLE_STYLES, TOGGLE_BUTTON_BASE } from '@/styles/toggleStyles';

export type ChartTableView = 'chart' | 'table';

interface ChartTableToggleProps {
    /** Currently selected view */
    value: ChartTableView;
    /** Callback when view changes */
    onChange: (view: ChartTableView) => void;
    /** Optional localStorage key for persistence (e.g., 'chart-view-net-worth') */
    storageKey?: string;
    /** Disable all options */
    disabled?: boolean;
}

const options: { value: ChartTableView; icon: typeof ChartBarIcon; ariaLabel: string }[] = [
    { value: 'chart', icon: ChartBarIcon, ariaLabel: 'Chart view' },
    { value: 'table', icon: TableCellsIcon, ariaLabel: 'Table view' },
];

/**
 * Toggle component for switching between chart and table views.
 * Styled as a compact button group following the GranularityToggle pattern.
 * Optionally persists preference in localStorage.
 */
export function ChartTableToggle({ value, onChange, storageKey, disabled = false }: ChartTableToggleProps) {
    return (
        <div className={`${TOGGLE_STYLES.container} ${disabled ? 'opacity-50' : ''}`} role="group" aria-label="View toggle">
            {options.map((option) => {
                const isActive = value === option.value;
                const Icon = option.icon;
                return (
                    <button
                        key={option.value}
                        type="button"
                        disabled={disabled}
                        onClick={() => {
                            if (disabled) return;
                            onChange(option.value);
                            if (storageKey) {
                                try {
                                    localStorage.setItem(storageKey, option.value);
                                } catch {
                                    // localStorage might be unavailable
                                }
                            }
                        }}
                        className={`
                            w-9 h-8 flex items-center justify-center ${TOGGLE_BUTTON_BASE}
                            ${isActive ? TOGGLE_STYLES.active : TOGGLE_STYLES.inactive}
                            ${disabled ? 'cursor-not-allowed' : ''}
                        `}
                        aria-pressed={isActive}
                        aria-label={option.ariaLabel}
                        title={disabled ? 'Not enough data' : undefined}
                    >
                        <Icon className="h-4 w-4" />
                    </button>
                );
            })}
        </div>
    );
}

/**
 * Hook for managing chart/table view preference with localStorage persistence.
 * @param storageKey - The localStorage key (e.g., 'chart-view-net-worth')
 * @param defaultValue - Default view if no stored preference (default: 'chart')
 * @returns [currentView, setView] tuple
 */
export function useViewPreference(
    storageKey: string,
    defaultValue: ChartTableView = 'chart'
): [ChartTableView, (view: ChartTableView) => void] {
    const [view, setViewState] = useState<ChartTableView>(() => {
        try {
            const stored = localStorage.getItem(storageKey);
            if (stored === 'chart' || stored === 'table') {
                return stored;
            }
        } catch {
            // localStorage might be unavailable
        }
        return defaultValue;
    });

    const setView = (newView: ChartTableView) => {
        setViewState(newView);
        try {
            localStorage.setItem(storageKey, newView);
        } catch {
            // localStorage might be unavailable
        }
    };

    // Sync with localStorage on mount if key changes
    useEffect(() => {
        try {
            const stored = localStorage.getItem(storageKey);
            if (stored === 'chart' || stored === 'table') {
                setViewState(stored);
            }
        } catch {
            // localStorage might be unavailable
        }
    }, [storageKey]);

    return [view, setView];
}
